import type { ApiFetchStoriesPort } from '~/handlers/ports/api-fetch-stories.port';
import { getApostatudoApiUrl } from '~/utils/apostatudo/get-apostatudo-api-url';

/**
 * Normaliza a URL da mídia para sempre usar a variante /public.
 * Remove qualquer variante existente (/public, /mobile, /ipad) e adiciona /public.
 * Se a URL não tiver variante, adiciona /public ao final.
 */
function normalizeMediaUrl(url: string): string {
  if (!url) return url;

  const normalized = url.replace(/\/(public|mobile|ipad)$/, '');

  return `${normalized}/public`;
}

/**
 * Verifica se um vídeo está disponível tentando carregar os metadados.
 * Vídeos recém-criados na Cloudflare podem não estar prontos ainda mesmo tendo URL.
 * Usa um elemento <video> temporário pois HEAD requests podem ser bloqueados por CORS.
 */
function isVideoAvailable(url: string): Promise<boolean> {
  if (!url) return Promise.resolve(false);

  return new Promise((resolve) => {
    const video = document.createElement('video');
    const timeout = setTimeout(() => {
      video.removeAttribute('src');
      video.load();
      resolve(false);
    }, 5000);

    video.onloadedmetadata = () => {
      clearTimeout(timeout);
      video.removeAttribute('src');
      video.load();
      resolve(true);
    };

    video.onerror = () => {
      clearTimeout(timeout);
      resolve(false);
    };

    video.preload = 'metadata';
    video.src = url;
  });
}

export class ApostatudoApiFetchStoriesAdapter implements ApiFetchStoriesPort {
  async handle() {
    const response = await fetch(`${getApostatudoApiUrl()}/media/type/STORY`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    const data = await response.json();

    const stories = data
      .filter((item: any) => !!item.url)
      .map((item: any) => {
        const mediaType = item.media_type || 'image';

        return {
          id: item.id,
          title: item.filename,
          description: item.filename || null,
          linkUrl: item.link_url || null,
          buttonText: item.button_text || null,
          buttonColor: item.button_color || null,
          buttonTextColor: item.button_text_color || null,
          imgUrl:
            mediaType === 'image' ? normalizeMediaUrl(item.url) : item.url,
          media_type: mediaType,
        };
      });

    // Verifica disponibilidade dos vídeos em paralelo
    const availabilityChecks = await Promise.all(
      stories.map(async (story: any) => {
        if (story.media_type === 'video') {
          return await isVideoAvailable(story.imgUrl);
        }

        return true;
      }),
    );

    return stories.filter((_: any, index: number) => availabilityChecks[index]);
  }
}
